import { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { Modal } from "@wine2trace/ui/components";
import { ActionButton } from "@wine2trace/ui/FormComponents";
import useLabels from "../../hooks/useLabels";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { selectActiveWinery, selectConfigs } from "../../context/selectors";
import { getStickyNote, updateStickyNote } from "../../services/utils";
import { getItem, setItem } from "../../services/LocalStorage";
import { toastAxios } from "../../services/toastAxios";
import "./styles/index.scss";

// Personal notes of the active winery: a white card like the detail sections (title, winery, the note, its last
// change); "save" stores the note and closes, as the old close button did.

const StickyNotesModal = ({ isOpen, onClose, children }) => {
  const [getLabel] = useLabels();
  const titleId = useId();
  const modalRef = useRef(null);
  const [noteText, setNoteText] = useState("");
  const [noteModifiedDate, setNoteModifiedDate] = useState();
  const axiosPrivate = useAxiosPrivate();
  const activeWinery = useSelector(selectActiveWinery);
  const configs = useSelector(selectConfigs);

  const MAX_LEN = 1000;

  useEffect(() => {
    const modalElement = modalRef.current;

    const fetchStickyNote = async () => {
      if (!isOpen || !modalElement) return;

      try {
        const response = await getStickyNote(axiosPrivate);
        const note = response?.data?.[0];

        if (note) {
          setNoteText(note.content || "");
          setNoteModifiedDate(note.last_modified || null);
        }
      } catch (err) {
        console.error("Failed to fetch sticky note:", err);
      }
    };

    fetchStickyNote();
  }, []);

  const customIdStickyNoteError = "custom-id-sticky-note-error";

  useEffect(() => {
    const modalElement = modalRef.current;

    if (modalElement) {
      if (isOpen) {
        modalElement.close();
        modalElement.showModal();

        try {
          const loadedNotes = getItem(`stickyNotes.${activeWinery?.id}`);
          const noteModifiedDate = getItem(
            `stickyNotesLastModified.${activeWinery?.id}`
          );
          setNoteText(loadedNotes?.trim());
          setNoteModifiedDate(noteModifiedDate);
        } catch (err) {
          console.log("err", err);
        }
      } else {
        modalElement.close();
      }
    }
  }, [isOpen]);

  const storeStickyNote = async () => {
    const trimmedNote = noteText?.trim();

    if (trimmedNote?.length > MAX_LEN) {
      return;
    }

    const requestPayload = {
      content: trimmedNote,
    };

    if (noteText?.length > 0) {
      const response = await toastAxios(
        () => updateStickyNote(requestPayload, axiosPrivate),
        customIdStickyNoteError,
        null,
        getLabel,
      );

      setItem(`stickyNotes.${activeWinery?.id}`, noteText?.trim());
      setItem(`stickyNotesLastModified.${activeWinery?.id}`, new Date());
    }

    onClose();
  };

  // Escape: the dialog would close by itself, unsaved and without telling the page; it saves and closes as Save.
  useEffect(() => {
    const modalElement = modalRef.current;
    if (!isOpen || !modalElement) return undefined;
    const onCancel = (event) => {
      event.preventDefault();
      storeStickyNote();
    };
    modalElement.addEventListener("cancel", onCancel);
    return () => modalElement.removeEventListener("cancel", onCancel);
  });

  return (
    isOpen && (
      <Modal
        forwardRef={modalRef}
        closeHandle={onClose}
        isOpen={isOpen}
        customClassName={"sticky_notes"}
      >
        <section className="sticky_notes_card" aria-labelledby={titleId}>
          <div className="sticky_notes_header">
            <h3 id={titleId} className="detail_section_title">{getLabel("personalNotes")}</h3>
            <p className="sticky_notes_winery">{activeWinery?.name}</p>
          </div>

          <textarea
            aria-labelledby={titleId}
            maxLength={MAX_LEN}
            className="sticky_notes_text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={getLabel("personalNotesPlaceholder")}
          />

          <div className="sticky_notes_footer">
            {noteText?.trim()?.length > MAX_LEN && (
              <span className="sticky_notes_overflow">
                -{noteText?.trim()?.length - MAX_LEN}
              </span>
            )}

            <span className="sticky_notes_modified">
              {getLabel("personalNotesModifiedDate")}{" "}
              {noteModifiedDate
                ? format(new Date(noteModifiedDate), configs.dateFormat)
                : "—"}
            </span>

            <ActionButton variant="primary" icon="fa-device-floppy" onClick={storeStickyNote}>
              {getLabel("detailSave")}
            </ActionButton>
          </div>
        </section>
      </Modal>
    )
  );
};

export default StickyNotesModal;
